import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { Loader } from '@/components/layout/loading';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { backupPolicyNativeProvider, isContinuousPolicyType, isGoogleWorkspacePolicyType, isNativePolicyType, isPitrPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onDuplicate?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => void;
}
export function BackupPolicyCard(props) {
  const $ = _c(159);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== allBackupPolicyProperties || $[1] !== enabled || $[2] !== isAuthorized || $[3] !== props || $[4] !== snackbar || $[5] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t13;
    if ($[25] !== props.policy.condition) {
      t13 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[25] = props.policy.condition;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    const resourcesScopeType = t13;
    let t14;
    if ($[27] !== props.policy.policyType) {
      t14 = isGoogleWorkspacePolicyType(props.policy.policyType);
      $[27] = props.policy.policyType;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const isGoogleWorkspace = t14;
    let t15;
    if ($[29] !== props || $[30] !== snackbar) {
      t15 = () => {
        const enabled_0 = props.policy.enabled;
        props.onEnabledPolicyUpdate?.(props.policy);
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[29] = props;
      $[30] = snackbar;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    togglePolicyEnabledState = t15;
    let t16;
    if ($[32] !== enabled || $[33] !== togglePolicyEnabledState) {
      t16 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[32] = enabled;
      $[33] = togglePolicyEnabledState;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    const onToggleClicked = t16;
    const t17 = props.policy?.id || "";
    let T5;
    let T6;
    let t18;
    let t19;
    let t20;
    let t21;
    let t22;
    let t23;
    if ($[35] !== enabled || $[36] !== isAuthorized || $[37] !== isGoogleWorkspace || $[38] !== props.onDelete || $[39] !== props.onDuplicate || $[40] !== props.onEdit || $[41] !== props.onEnabledPolicyUpdate || $[42] !== props.policy.attachedResources || $[43] !== props.policy.name || $[44] !== props.policy.policyType || $[45] !== props.processing || $[46] !== t17) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t17],
        property: backupPolicies.name
      };
      T4 = BPCard;
      t11 = !enabled;
      t12 = "backup-policy-card";
      T6 = Stack;
      t21 = "row";
      if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[58] = t22;
      } else {
        t22 = $[58];
      }
      let t24;
      if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[59] = t24;
      } else {
        t24 = $[59];
      }
      let t25;
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[60] = t25;
      } else {
        t25 = $[60];
      }
      let t26;
      if ($[61] !== props.policy.name) {
        t26 = <Stack sx={t25}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[61] = props.policy.name;
        $[62] = t26;
      } else {
        t26 = $[62];
      }
      let t27;
      if ($[63] !== props.policy.policyType) {
        t27 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[63] = props.policy.policyType;
        $[64] = t27;
      } else {
        t27 = $[64];
      }
      if ($[65] !== t26 || $[66] !== t27) {
        t23 = <Stack direction="row" sx={t24}>{t26}{t27}</Stack>;
        $[65] = t26;
        $[66] = t27;
        $[67] = t23;
      } else {
        t23 = $[67];
      }
      T5 = Stack;
      t18 = "row";
      if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = {
          alignItems: "center",
          gap: "16px"
        };
        $[68] = t19;
      } else {
        t19 = $[68];
      }
      t20 = enabled && !isGoogleWorkspace && <>{props.processing ? <Loader variant="inline" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createBackupStatusNotInCondition([BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.Unsupported, BackupStatus.LimitExceeded])])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[35] = enabled;
      $[36] = isAuthorized;
      $[37] = isGoogleWorkspace;
      $[38] = props.onDelete;
      $[39] = props.onDuplicate;
      $[40] = props.onEdit;
      $[41] = props.onEnabledPolicyUpdate;
      $[42] = props.policy.attachedResources;
      $[43] = props.policy.name;
      $[44] = props.policy.policyType;
      $[45] = props.processing;
      $[46] = t17;
      $[47] = T4;
      $[48] = T5;
      $[49] = T6;
      $[50] = t11;
      $[51] = t12;
      $[52] = t18;
      $[53] = t19;
      $[54] = t20;
      $[55] = t21;
      $[56] = t22;
      $[57] = t23;
    } else {
      T4 = $[47];
      T5 = $[48];
      T6 = $[49];
      t11 = $[50];
      t12 = $[51];
      t18 = $[52];
      t19 = $[53];
      t20 = $[54];
      t21 = $[55];
      t22 = $[56];
      t23 = $[57];
    }
    let t24;
    if ($[69] !== isAuthorized || $[70] !== isGoogleWorkspace || $[71] !== props) {
      t24 = (isGoogleWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isGoogleWorkspace ? [] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[69] = isAuthorized;
      $[70] = isGoogleWorkspace;
      $[71] = props;
      $[72] = t24;
    } else {
      t24 = $[72];
    }
    let t25;
    if ($[73] !== enabled || $[74] !== isAuthorized || $[75] !== onToggleClicked || $[76] !== props.onEnabledPolicyUpdate) {
      t25 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[73] = enabled;
      $[74] = isAuthorized;
      $[75] = onToggleClicked;
      $[76] = props.onEnabledPolicyUpdate;
      $[77] = t25;
    } else {
      t25 = $[77];
    }
    let t26;
    if ($[78] !== T5 || $[79] !== t18 || $[80] !== t19 || $[81] !== t20 || $[82] !== t24 || $[83] !== t25) {
      t26 = <T5 direction={t18} sx={t19}>{t20}{t24}{t25}</T5>;
      $[78] = T5;
      $[79] = t18;
      $[80] = t19;
      $[81] = t20;
      $[82] = t24;
      $[83] = t25;
      $[84] = t26;
    } else {
      t26 = $[84];
    }
    if ($[85] !== T6 || $[86] !== t21 || $[87] !== t22 || $[88] !== t23 || $[89] !== t26) {
      t10 = <T6 direction={t21} sx={t22}>{t23}{t26}</T6>;
      $[85] = T6;
      $[86] = t21;
      $[87] = t22;
      $[88] = t23;
      $[89] = t26;
      $[90] = t10;
    } else {
      t10 = $[90];
    }
    T3 = CardContent;
    if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        marginTop: "24px"
      };
      $[91] = t7;
    } else {
      t7 = $[91];
    }
    let t27;
    if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[92] = t27;
    } else {
      t27 = $[92];
    }
    let t28;
    if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = {
        gap: "24px"
      };
      $[93] = t28;
    } else {
      t28 = $[93];
    }
    let t29;
    if ($[94] !== allBackupPolicyProperties || $[95] !== props.policy.condition || $[96] !== props.policy.exclude || $[97] !== props.policy.include || $[98] !== props.policy.policyType || $[99] !== props.policy.resourceTypes || $[100] !== resourcesScopeType) {
      t29 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[94] = allBackupPolicyProperties;
      $[95] = props.policy.condition;
      $[96] = props.policy.exclude;
      $[97] = props.policy.include;
      $[98] = props.policy.policyType;
      $[99] = props.policy.resourceTypes;
      $[100] = resourcesScopeType;
      $[101] = t29;
    } else {
      t29 = $[101];
    }
    let t30;
    if ($[102] !== props.policy.policyType) {
      t30 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} />;
      $[102] = props.policy.policyType;
      $[103] = t30;
    } else {
      t30 = $[103];
    }
    let t31;
    if ($[104] !== t29 || $[105] !== t30) {
      t31 = <AccordionDetails><Stack sx={t28}>{t29}{t30}</Stack></AccordionDetails>;
      $[104] = t29;
      $[105] = t30;
      $[106] = t31;
    } else {
      t31 = $[106];
    }
    if ($[107] !== props.defaultExpanded || $[108] !== t31) {
      t8 = <Accordion defaultExpanded={props.defaultExpanded}>{t27}{t31}</Accordion>;
      $[107] = props.defaultExpanded;
      $[108] = t31;
      $[109] = t8;
    } else {
      t8 = $[109];
    }
    if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider sx={{
        margin: "12px -24px"
      }} />;
      $[110] = t9;
    } else {
      t9 = $[110];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[111] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[111] = vaultsSectionExpanded;
      $[112] = t6;
    } else {
      t6 = $[112];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[113] = t2;
      $[114] = t3;
    } else {
      t2 = $[113];
      t3 = $[114];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = allBackupPolicyProperties;
    $[1] = enabled;
    $[2] = isAuthorized;
    $[3] = props;
    $[4] = snackbar;
    $[5] = vaultsSectionExpanded;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = T4;
    $[11] = t0;
    $[12] = t1;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = togglePolicyEnabledState;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    T4 = $[10];
    t0 = $[11];
    t1 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
    togglePolicyEnabledState = $[24];
  }
  let t13;
  if ($[115] !== T0 || $[116] !== t0 || $[117] !== t1 || $[118] !== t2 || $[119] !== t3 || $[120] !== t4) {
    t13 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[115] = T0;
    $[116] = t0;
    $[117] = t1;
    $[118] = t2;
    $[119] = t3;
    $[120] = t4;
    $[121] = t13;
  } else {
    t13 = $[121];
  }
  let t14;
  if ($[122] !== T1 || $[123] !== t13) {
    t14 = <T1>{t13}</T1>;
    $[122] = T1;
    $[123] = t13;
    $[124] = t14;
  } else {
    t14 = $[124];
  }
  let t15;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[125] = t15;
  } else {
    t15 = $[125];
  }
  let t16;
  if ($[126] !== props.policy || $[127] !== props.vaults) {
    t16 = <AccordionDetails sx={t15}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[126] = props.policy;
    $[127] = props.vaults;
    $[128] = t16;
  } else {
    t16 = $[128];
  }
  let t17;
  if ($[129] !== T2 || $[130] !== t14 || $[131] !== t16 || $[132] !== t5 || $[133] !== t6) {
    t17 = <T2 expanded={t5} onChange={t6}>{t14}{t16}</T2>;
    $[129] = T2;
    $[130] = t14;
    $[131] = t16;
    $[132] = t5;
    $[133] = t6;
    $[134] = t17;
  } else {
    t17 = $[134];
  }
  let t18;
  if ($[135] !== T3 || $[136] !== t17 || $[137] !== t7 || $[138] !== t8 || $[139] !== t9) {
    t18 = <T3 sx={t7}>{t8}{t9}{t17}</T3>;
    $[135] = T3;
    $[136] = t17;
    $[137] = t7;
    $[138] = t8;
    $[139] = t9;
    $[140] = t18;
  } else {
    t18 = $[140];
  }
  let t19;
  if ($[141] !== props.policy.name) {
    t19 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[141] = props.policy.name;
    $[142] = t19;
  } else {
    t19 = $[142];
  }
  let t20;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setDisablePolicyDialogOpen(false);
    $[143] = t20;
  } else {
    t20 = $[143];
  }
  let t21;
  if ($[144] !== togglePolicyEnabledState) {
    t21 = () => {
      togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[144] = togglePolicyEnabledState;
    $[145] = t21;
  } else {
    t21 = $[145];
  }
  let t22;
  if ($[146] !== disablePolicyDialogOpen || $[147] !== t19 || $[148] !== t21) {
    t22 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t19} onCancel={t20} onConfirm={t21} buttonTitle="Disable Policy" />;
    $[146] = disablePolicyDialogOpen;
    $[147] = t19;
    $[148] = t21;
    $[149] = t22;
  } else {
    t22 = $[149];
  }
  let t23;
  if ($[150] !== t10 || $[151] !== t18 || $[152] !== t22) {
    t23 = <>{t10}{t18}{t22}</>;
    $[150] = t10;
    $[151] = t18;
    $[152] = t22;
    $[153] = t23;
  } else {
    t23 = $[153];
  }
  let t24;
  if ($[154] !== T4 || $[155] !== t11 || $[156] !== t12 || $[157] !== t23) {
    t24 = <T4 disabled={t11} data-testid={t12}>{t23}</T4>;
    $[154] = T4;
    $[155] = t11;
    $[156] = t12;
    $[157] = t23;
    $[158] = t24;
  } else {
    t24 = $[158];
  }
  return t24;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}